import { useEffect, useState } from "react";
import { Box, Drawer, Toolbar, useMediaQuery } from "@mui/material";
import { Outlet } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import useMaintenance from "features/maintenance/hooks/useMaintenance";
import createMaintenanceService from "services/maintenance/createMaintenanceService";
import { setMaintenanceData } from "features/maintenance/dataStore/maintenanceSlice";
import { useDispatch, useSelector } from "react-redux";
import { API_ROUTES } from "utils/constants";
import { startSignalR } from "services/signalR/signalRService";
import { ReadMessages } from "features/Messages/ReadMessages";

const DRAWER_WIDTH = 250;

const EXPANDED_WIDTH = DRAWER_WIDTH;
const COLLAPSED_WIDTH = 68;

export default function DashboardLayout() {
    const [mobileOpen, setMobileOpen] = useState(false);
    const dispatch = useDispatch();

    const { search, items, loading } = useMaintenance({
        service: createMaintenanceService(API_ROUTES.MAINTENANCE_FETCH_ALL)
    });

    useEffect(() => {
        console.log("signalR");
        startSignalR();
        search();
    }, []);

    useEffect(() => {
        if (items) {
            dispatch(setMaintenanceData(items));
        }
    }, [items]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Automatically default to collapsed mode on small screens
    const [isCollapsed, setIsCollapsed] = useState(isMobile);

    const toggleCollapse = () => {
        setIsCollapsed((prev) => !prev);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const currentWidth = isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    if (loading) return "Loading...";

    const renderToggleButton = () => {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 40, // smaller height
                    width: "100%",
                    p: 0, // remove default padding
                    m: 0 // remove auto margins,
                }}
            >
                <IconButton
                    onClick={toggleCollapse}
                    color="primary"
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: "primary.main",
                        color: "white",
                        boxShadow: 2,
                        "&:hover": {
                            bgcolor: "primary.dark",
                            transform: "scale(1.05)",
                            transition: "all 0.2s ease-in-out"
                        }
                    }}
                >
                    {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>
            </Box>
        );
    };

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F4F4F4" }}>
            {/* Full-width header */}
            <Header
                drawerWidth={currentWidth}
                onMenuClick={handleDrawerToggle}
            />

            {/* Responsive Sidebar Wrapper */}
            <Box
                component="nav"
                sx={{ width: { sm: currentWidth }, flexShrink: { sm: 0 } }}
            >
                {/* Mobile Temporary Drawer */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        // display: { xs: "block", sm: "none" },
                        width: currentWidth,
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                        "& .MuiDrawer-paper1": {
                            boxSizing: "border-box",
                            width: DRAWER_WIDTH
                        },
                        "& .MuiDrawer-paper": {
                            width: currentWidth,
                            transition: theme.transitions.create("width", {
                                easing: theme.transitions.easing.sharp,
                                duration:
                                    theme.transitions.duration.enteringScreen
                            }),
                            overflowX: "hidden"
                        }
                    }}
                >
                    <Toolbar /> {/* Spacer: keeps mobile menu below header */}
                    <IconButton onClick={toggleCollapse} color="primary">
                        {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                    <Sidebar isCollapsed={isCollapsed} />
                </Drawer>

                {/* Desktop Permanent Drawer */}
                <Drawer
                    variant="permanent"

                    sx={{
                        width: currentWidth,
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper1": {
                            boxSizing: "border-box",
                            width: DRAWER_WIDTH
                        },
                        "& .MuiDrawer-paper": {
                            width: currentWidth,
                            transition: theme.transitions.create("width", {
                                easing: theme.transitions.easing.sharp,
                                duration:
                                    theme.transitions.duration.enteringScreen
                            }),
                            overflowX: "hidden"
                        }
                    }}
                    open
                >
                    <Toolbar /> {/* Spacer: keeps desktop menu below header */}
                    {renderToggleButton()}
                    <Sidebar isCollapsed={isCollapsed} />
                </Drawer>
            </Box>

            {/* Main Content Area */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    // Content automatically fills remaining width on desktop
                    width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` }
                }}
            >
                <Toolbar /> {/* Spacer: keeps page text below header */}
                <Outlet />
            </Box>
        </Box>
    );
}
